import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Send, Copy, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-contained dialog for sending a note to the client for review/signature.
 *
 * Lives outside the App component's reconciliation tree so typing the
 * "Instructions for Client" message only re-renders this dialog —
 * NOT the 86k-line App monolith. This is the perf fix for the
 * "typing is extremely slow" complaint.
 *
 * May 2026 — surfaces the post-send deep link with a "Copy link"
 * button so the provider can SMS / manually share the link when
 * email isn't reliable.  The link itself comes from the backend
 * (``POST /notes/{id}/send-to-client`` → ``portal_link``) so the
 * dialog never has to know about JWT signing / magic-link
 * structure.
 *
 * Props:
 *   open          — dialog visibility (controlled by parent)
 *   onOpenChange  — (bool) => void; called when dialog should close
 *   noteId        — note to send
 *   onSuccess     — (noteId) => void; called after successful send so
 *                   the parent can refresh lists / close the note detail.
 *                   Failures inside this callback are swallowed (silent
 *                   console.warn) so they DO NOT trigger a "Failed to send"
 *                   toast — the actual send already succeeded.
 */
export const SendToClientDialog = React.memo(function SendToClientDialog({
  open,
  onOpenChange,
  noteId,
  onSuccess,
  alreadySigned = false,
}) {
  const [clientMessage, setClientMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // After a successful send we surface the deep link so the provider
  // can copy it for SMS / chat.  Cleared on dialog close + reopen.
  const [portalLink, setPortalLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reset state when dialog re-opens for a new note
  useEffect(() => {
    if (!open) {
      setClientMessage('');
      setPortalLink(null);
      setCopied(false);
    }
  }, [open]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCopyLink = useCallback(async () => {
    if (!portalLink) return;
    try {
      await navigator.clipboard.writeText(portalLink);
      setCopied(true);
      toast.success('Link copied to clipboard');
      // Revert the icon after a moment so a second copy still feels
      // responsive.
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Older browsers / insecure contexts (HTTP) — fall back to a
      // hidden textarea + execCommand so HIPAA-locked devices that
      // still run IE11 can copy too.
      try {
        const ta = document.createElement('textarea');
        ta.value = portalLink;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        toast.success('Link copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error('Could not copy link automatically — please select & copy it manually');
      }
    }
  }, [portalLink]);

  const handleSubmit = useCallback(async () => {
    if (!noteId) return;
    setSubmitting(true);
    let sendResponse;
    try {
      sendResponse = await axios.post(`${API}/notes/${noteId}/send-to-client`, null, {
        params: { client_message: clientMessage.trim() || null },
      });
      toast.success(
        sendResponse?.data?.copy_only
          ? 'Signed copy sent to client (no new signature requested)'
          : 'Document sent to client'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send document');
      setSubmitting(false);
      return;
    }
    // Surface the deep link if the backend returned one — the provider
    // can copy it now (or just close out and trust the email).
    const link = sendResponse?.data?.portal_link;
    if (link) {
      setPortalLink(link);
    }
    // Post-success cleanup. Failures here MUST NOT show "Failed to send".
    try {
      onSuccess?.(noteId);
    } catch (e) {
      console.warn('Post-send cleanup failed (document was sent successfully):', e);
    }
    setSubmitting(false);
    // Don't auto-close — leave the dialog open so the provider can
    // optionally copy the deep link.  They can dismiss with the
    // explicit "Done" button.  If no link came back (offline / older
    // backend / no email) we close immediately so the original UX
    // is preserved.
    if (!link) {
      onOpenChange(false);
    }
  }, [noteId, clientMessage, onSuccess, onOpenChange]);

  const handleDone = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Block dismissal (Escape, click-outside, X) while submitting so
        // the user can't accidentally close mid-send and re-click.
        if (submitting && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-md"
        // Block all interaction with the dialog body while submitting —
        // makes it visually obvious the request is in flight even on a
        // slow connection where the toast doesn't appear for several seconds.
        aria-busy={submitting}
      >
        <DialogHeader>
          <DialogTitle>
            {portalLink
              ? 'Document Sent — Share the Link?'
              : alreadySigned
                ? 'Send Signed Copy to Client'
                : 'Send Document to Client'}
          </DialogTitle>
        </DialogHeader>

        {/* Pre-send body */}
        {!portalLink && (
          <>
            <div className={`space-y-4 transition-opacity ${submitting ? 'opacity-60 pointer-events-none' : ''}`}>
              {alreadySigned ? (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800" data-testid="send-to-client-signed-copy-notice">
                  This document has already been signed. The client will receive a
                  copy of the signed document by email and in their portal —
                  <strong> no new signature will be requested</strong>.
                </div>
              ) : (
                <p className="text-sm text-gray-600">
                  This document will be sent to the client for their review and signature.
                </p>
              )}
              <div>
                <Label htmlFor="clientMessage">Instructions for Client (optional)</Label>
                <textarea
                  id="clientMessage"
                  data-testid="send-to-client-message-textarea"
                  className="w-full min-h-[120px] p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 mt-2 disabled:bg-gray-50"
                  placeholder="e.g., Please review section 3 and sign at the bottom..."
                  value={clientMessage}
                  onChange={(e) => setClientMessage(e.target.value)}
                  autoFocus
                  disabled={submitting}
                />
              </div>
            </div>
            {/* Footer is OUTSIDE the dimmed body so the user can still see
                the loading state and the disabled buttons clearly. */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                data-testid="send-to-client-cancel-btn"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 min-w-[160px]"
                onClick={handleSubmit}
                data-testid="send-to-client-submit-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending to client…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {alreadySigned ? 'Send Copy' : 'Send to Client'}
                  </>
                )}
              </Button>
            </div>
          </>
        )}

        {/* Post-send body — copy-link affordance */}
        {portalLink && (
          <>
            <div className="space-y-4">
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                ✓ The document was emailed to the client. If you want to
                share it another way (SMS, secure chat), copy the link
                below — it drops them straight onto the signature page.
              </div>
              <div>
                <Label htmlFor="portalLink" className="text-xs text-gray-500">
                  Deep link (expires in 72 hours)
                </Label>
                <div className="mt-1 flex items-stretch gap-2">
                  <input
                    id="portalLink"
                    type="text"
                    readOnly
                    value={portalLink}
                    data-testid="send-to-client-portal-link-input"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 border rounded-md bg-gray-50 text-sm font-mono text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCopyLink}
                    data-testid="send-to-client-copy-link-btn"
                    className="shrink-0"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4 mr-2 text-green-600" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Anyone with this link can sign as the client — share
                  only over secure channels.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                onClick={handleDone}
                data-testid="send-to-client-done-btn"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default SendToClientDialog;
